/**
 * @fileoverview Video Player Component - Premium Learning Experience with React Player
 * @module VideoPlayer
 * 
 * Design Philosophy:
 * - Industry-standard React Player for reliable video playback
 * - Maintains sophisticated dark theme consistency
 * - Professional video player with progress tracking
 * - Handles video URL changes seamlessly
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactPlayer from 'react-player';
import { Play, AlertCircle } from 'lucide-react';

interface VideoPlayerProps {
  videoUrl: string;
  currentProgress: number;
  onProgress: (progress: number) => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
  onSectionChange?: (sectionId: string) => void;
  onVideoComplete?: () => void;
  autoPlay?: boolean;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ 
  videoUrl, 
  currentProgress, 
  onProgress,
  onTimeUpdate,
  onVideoComplete,
  autoPlay = false 
}) => {
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [played, setPlayed] = useState(0);
  const [loaded, setLoaded] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const playerRef = useRef<any>(null);

  // Handle progress updates
  const handleProgress = useCallback((state: any) => {
    setPlayed(state.played);
    setLoaded(state.loaded);
    
    // Update progress percentage
    const progressPercent = Math.round(state.played * 100);
    onProgress(progressPercent);
    
    // Call time update handler if provided
    if (onTimeUpdate && duration > 0) {
      onTimeUpdate(state.playedSeconds, duration);
    }
  }, [onProgress, onTimeUpdate, duration]);

  // Handle video completion
  const handleVideoEnded = useCallback(() => {
    console.log('🎯 Video completed!');
    setPlaying(false);
    if (onVideoComplete) {
      onVideoComplete();
    }
  }, [onVideoComplete]);

  // Handle when video duration is available
  const handleDuration = useCallback((event: any) => {
    // ReactPlayer v3 wires `onDurationChange` to the underlying HTMLMediaElement event,
    // so the callback receives a SyntheticEvent. Safely extract the duration.
    const videoDuration = typeof event === 'number' ? event : event?.currentTarget?.duration ?? 0;
    setDuration(videoDuration);
    console.log('Video duration loaded:', videoDuration);
  }, []);

  // Handle when video is ready to play
  const handleReady = useCallback(() => {
    setIsReady(true);
    setHasError(false);
    console.log('✅ Video ready to play:', videoUrl);
  }, [videoUrl]);

  // Handle video errors
  const handleError = useCallback((error: any, data?: any, hlsInstance?: any, hlsGlobal?: any) => {
    console.error('❌ Video player error:', {
      error,
      data,
      videoUrl,
      timestamp: new Date().toISOString()
    });
    
    // Check for CORS errors specifically
    if (error && typeof error === 'object') {
      if (error.message?.includes('CORS') || error.message?.includes('Cross-Origin')) {
        console.error('🚫 CORS Error detected - S3 bucket may need CORS configuration');
      }
      if (error.message?.includes('403') || error.message?.includes('Forbidden')) {
        console.error('🔒 Access forbidden - check S3 bucket permissions');
      }
    }
    
    setHasError(true);
    setPlaying(false);
    setIsReady(false);
  }, [videoUrl]);

  // Reset state when video URL changes
  useEffect(() => {
    if (videoUrl) {
      console.log('Video URL changed, resetting player state:', videoUrl);
      setIsReady(false);
      setHasError(false);
      setPlaying(false);
      setPlayed(0);
      setDuration(0);
    }
  }, [videoUrl]);

  // Handle play/pause
  const handlePlayPause = useCallback(() => {
    if (!videoUrl || (!videoUrl.includes('http') && !ReactPlayer.canPlay?.(videoUrl))) {
      console.log('No valid video URL to play');
      return;
    }
    
    if (hasError) {
      console.log('Video has error, cannot play');
      return;
    }
    
    setPlaying(!playing);
  }, [playing, videoUrl, hasError]);

  // Handle seek
  const handleSeekChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const seekTo = parseFloat(e.target.value);
    setPlayed(seekTo);
    if (playerRef.current) {
      playerRef.current.seekTo(seekTo, 'fraction');
    }
  }, []);

  // Format time display
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Check if we have a valid video URL
  const hasValidUrl = videoUrl && (videoUrl.includes('http') || ReactPlayer.canPlay?.(videoUrl));
console.log('RENDERING VIDEO PLAYER', videoUrl)
  return (
    <div className="relative bg-black aspect-video w-full overflow-hidden shadow-2xl rounded-lg">
      {/* Video Player */}
      {hasValidUrl && !hasError ? (
        <div className="relative w-full h-full">
          <ReactPlayer
            ref={playerRef}

            src={videoUrl}
            width="100%"
            height="100%"
            playing={playing}
            controls={true}
            light={false}
            pip={false}
            onReady={handleReady}
            onStart={() => console.log('🎬 Video started')}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            // @ts-ignore
            onProgress={handleProgress}
            onDurationChange={handleDuration}
            onEnded={handleVideoEnded}
            onError={handleError}
       
            style={{
              backgroundColor: '#1e293b'
            }}
          />
          
          {/* Custom overlay for better integration with design */}
          {!isReady && (
            <div className="absolute inset-0 bg-slate-900 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <h3 className="text-xl font-bold text-white mb-2">Loading Video...</h3>
                <p className="text-slate-400">Please wait while the video loads</p>
              </div>
            </div>
          )}
        </div>
      ) : hasError ? (
        /* Error State */
        <div className="absolute inset-0 bg-slate-900 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 border-2 border-red-300 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Video Playback Error</h3>
            <p className="text-slate-400">Unable to load or play this video</p>
            <p className="text-slate-300 text-sm mt-2">Please check your connection or try again later</p>
            <button 
              onClick={() => {
                setHasError(false);
                setIsReady(false);
                // Force re-render by updating a state
                setPlaying(false);
              }}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      ) : (
        /* Video Placeholder/No URL State */
        <div className="absolute inset-0 bg-slate-900 flex items-center justify-center">
          <div className="text-center">
            <Play className="w-16 h-16 text-slate-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Video Coming Soon</h3>
            <p className="text-slate-400">This module's video content is being prepared</p>
            <p className="text-slate-300 text-sm mt-2">Course content will be available soon</p>
          </div>
        </div>
      )}


    </div>
  );
};

export default VideoPlayer; 